import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  Stack,
} from "@material-ui/core";
import { LoadingButton } from "@mui/lab";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "src/redux/store";
import AddOnsPicker from "src/components/AddOnsPicker";
import { readPendingSelection, savePendingSelection } from "src/utils/pendingSelection";
import { getAddOns } from "src/redux/slices/addOns";
import { getPlans } from "src/redux/slices/plan";

// Wizard · Step 8 · Extras (add-ons no-soporte). Se muestra después del
// StepPlan, así que ya tenemos `pendingSelection.planId` guardado. Con
// el plan sabemos cuántos add-ons son GRATIS:
//   · Pro → 1 · Premium → 4 · Otros → 0
//
// Los primeros N seleccionados se pintan con badge "GRATIS con tu plan"
// y su precio tachado. Si el user se pasa, alerta amarilla con el
// coste extra €/mes.
//
// NO llama a SetAddOn — el user está en trial sin Stripe subscription.
// Solo persiste `addOnIds` en localStorage. La contratación real se
// hace al activar la suscripción en el portal Billing.

function inferTier(plan) {
  const n = (plan?.name || "").toLowerCase();
  if (n.includes("premium")) return "premium";
  if (n.includes("pro")) return "pro";
  if (n.includes("starter")) return "starter";
  return null;
}

function includedCountFor(tier) {
  if (tier === "premium") return 4;
  if (tier === "pro") return 1;
  return 0;
}

export default function StepAddOns({ onComplete, onSkip }) {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { addOns } = useSelector((s) => s.addOns);
  const { plans } = useSelector((s) => s.plan);
  const [selectedIds, setSelectedIds] = useState([]);
  const [initialised, setInitialised] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!addOns || addOns.length === 0) dispatch(getAddOns(true));
    if (!plans || plans.length === 0) dispatch(getPlans(false));
  }, [dispatch, addOns, plans]);

  const pending = useMemo(() => readPendingSelection(), []);
  const selectedPlan = useMemo(() => {
    if (!plans || plans.length === 0 || !pending?.planId) return null;
    return plans.find((p) => p.id === pending.planId) || null;
  }, [plans, pending]);
  const tier = inferTier(selectedPlan);
  const includedCount = includedCountFor(tier);

  // Hidratación inicial — pre-marca extras del pendingSelection
  // (soporte se queda fuera; su step propio lo lee).
  useEffect(() => {
    if (initialised) return;
    if (!addOns || addOns.length === 0) return;
    if (pending?.addOnIds?.length) {
      const nonSupport = pending.addOnIds.filter((id) => {
        const a = addOns.find((x) => x.id === id);
        return a && !a.kind?.startsWith("support");
      });
      setSelectedIds(nonSupport);
    }
    setInitialised(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addOns]);

  const extras = useMemo(() => {
    const extraIds = selectedIds.slice(includedCount);
    const extraCost = extraIds.reduce((sum, id) => {
      const a = addOns?.find((x) => x.id === id);
      return sum + (a ? Number(a.price) : 0);
    }, 0);
    return { extraIds, extraCost };
  }, [selectedIds, includedCount, addOns]);

  const handleContinue = () => {
    setSaving(true);
    try {
      const existing = readPendingSelection() || {
        addOnIds: [],
        savedAt: new Date().toISOString(),
      };
      // Preservamos el add-on de soporte (que vive en su step) al
      // fusionar. Sin este merge, guardar aquí lo borraría.
      const existingSupportIds = (existing.addOnIds || []).filter((id) => {
        const a = addOns?.find((x) => x.id === id);
        return a?.kind?.startsWith("support");
      });
      savePendingSelection({
        ...existing,
        addOnIds: [...selectedIds, ...existingSupportIds],
        savedAt: new Date().toISOString(),
      });
      onComplete?.({
        dataJson: JSON.stringify({
          extraIds: selectedIds,
          tier,
          includedCount,
        }),
      });
      if (selectedIds.length > 0) {
        enqueueSnackbar(
          `${selectedIds.length} extra(s) guardado(s). Se aplicarán al activar tu suscripción.`,
          { variant: "success" },
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const includedBanner = (() => {
    if (!selectedPlan) {
      return (
        <Alert severity="info" sx={{ mb: 3 }}>
          Aún no has elegido plan. Los add-ons se guardarán con su precio
          normal — Pro incluye 1 gratis y Premium los 4 al activar la
          suscripción.
        </Alert>
      );
    }
    if (includedCount === 0) {
      return (
        <Alert severity="info" sx={{ mb: 3 }}>
          Tu plan <strong>{selectedPlan.name}</strong> no incluye add-ons
          gratis. Los que marques se cobrarán al activar la suscripción.
        </Alert>
      );
    }
    if (includedCount >= 4) {
      return (
        <Alert severity="success" sx={{ mb: 3 }}>
          🎉 <strong>{selectedPlan.name}</strong> incluye TODOS los add-ons
          sin coste. Marca los que quieras usar.
        </Alert>
      );
    }
    return (
      <Alert severity="success" sx={{ mb: 3 }}>
        🎁 <strong>{selectedPlan.name}</strong> incluye <strong>{includedCount}</strong>{" "}
        add-on{includedCount === 1 ? "" : "s"} sin coste. Los primeros que
        marques son gratis; el resto suma al mensual.
      </Alert>
    );
  })();

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
        Extras opcionales
      </Typography>
      <Typography sx={{ color: "text.secondary", mb: 3 }}>
        Módulos adicionales que puedes activar según lo que necesites. Nada
        se cobra durante el trial — la contratación real se aplica al
        activar tu suscripción.
      </Typography>

      {includedBanner}

      <AddOnsPicker
        selectedIds={selectedIds}
        onChange={setSelectedIds}
        filter={(a) => !a.kind?.startsWith("support")}
        includedCount={includedCount}
        emptyLabel="Aún no hay extras disponibles."
      />

      {extras.extraIds.length > 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Has marcado <strong>{extras.extraIds.length}</strong> add-on(s) por
          encima de los incluidos → sumarán{" "}
          <strong>{extras.extraCost.toFixed(2)} €/mes</strong> a tu factura.
        </Alert>
      )}

      <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 4 }}>
        <Button color="inherit" onClick={onSkip} disabled={saving}>
          Saltar
        </Button>
        <LoadingButton
          variant="contained"
          loading={saving}
          onClick={handleContinue}
          sx={{ minWidth: 180 }}
        >
          Guardar extras
        </LoadingButton>
      </Stack>
    </Box>
  );
}
